import type { AddFlagProps } from '@databases/flags/types';
import { pasteContentExperience } from '@databases/frontend-metrics';
import type { MessageFormatter } from '@databases/intl/intl-types';
import { logWarning } from '@databases/logs';
import { createMentionNotification } from '@databases/mention-notification';
import { DefaultRootState } from '@databases/redux';
import {
  deselectAll,
  selectValueRange,
  selectValueSingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectHasSelectedCells,
  selectSelectedEntireEntryIds,
  selectSelectedEntireFieldIds,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { setDraftedChangeset } from '@databases/redux/changesets/actions';
import {
  selectDatabaseDraftByContentId,
  selectDraftedChangeset,
} from '@databases/redux/changesets/selectors';
import {
  Database,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
  SelectOption,
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  addChangeToChangeset,
  DatabaseChange,
  DatabaseChangeset,
  DatabaseDraft,
  MultiValueChangeType,
  ValueChange,
} from '@databases/utils/database/changesets';
import {
  convertType,
  getInitialValue,
  isEmptyValue,
} from '@databases/utils/database/field-types';
import { getNewOption } from '@databases/utils/database/field-types/select';
import {
  createEntry,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { getOrApplyValueChange } from '@databases/utils/deltas/paste-value-delta';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch, SimpleStore } from '@databases/utils/redux/redux';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { isUserArray } from '../helpers/fieldTypeValidation';
import {
  resolveEntryReference,
  resolveFieldReference,
  unreachable,
} from '../helpers/importHelper';
import {
  getImportSettingsFromSelection,
  getImportSettingsWithMissingItems,
  getLastEntryIdByChangeType,
  getLastFieldIdByChangeType,
} from '../helpers/pasteHelper';
import { messages } from '../messages';
import { applyImportSettings } from '../processes/applySetting';
import { parsePasteContent } from '../processes/parsePaste';
import { readPasteContent } from '../processes/readPaste';
import {
  ImportValues,
  ImportEntryMode,
  ImportEntryReference,
  ImportFieldMode,
  ImportResult,
  ImportSettings,
  ParsedImportFile,
} from '../types';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

/**
 * Returns the database changes mapped from the value changes of the pasted content.
 * Converts the pasted cell text value to the new field type if the cell value type is different from the field type.
 **/
export function getValueChanges(
  cell: ImportValues,
  settings: ImportSettings,
  entryIdx: number,
  fieldIdx: number,
  database: DatabaseDraft,
  store: SimpleStore<DefaultRootState>
): DatabaseChange[] {
  const entryRef = settings.entryReferences[entryIdx];
  const entry = resolveEntryReference(database, entryRef);
  const fieldRef = settings.fieldReferences[fieldIdx];
  const field = resolveFieldReference(database, fieldRef);

  if (!field || !entry) {
    return [];
  }

  const convertedValue =
    cell?.text && cell.value?.type !== field.type
      ? convertType(
          {
            database,
            field,
            value: {
              value: cell.text,
              type: DatabaseFieldType.TEXT,
            },
          },
          field.type,
          store.getState()
        )
      : cell.value!;

  const valueChange = getOrApplyValueChange(
    {
      database,
      field,
      entry,
      value: convertedValue,
      text: cell.text,
    },
    store
  );

  return (valueChange || []).map((change: ValueChange) => ({
    type: DatabaseChangeType.SET_VALUE,
    entryId: entry.id,
    value: convertedValue,
    fieldId: field.id,
    change,
  }));
}

/**
 * Returns the database change for adding a new entry with the pasted content as text values
 * */
const handleAddEntryChange = (
  entryIdx: number,
  databaseDraft: DatabaseDraft,
  settings: ImportSettings,
  importResult: ImportResult,
  entryRef: ImportEntryReference
): DatabaseChange => {
  const entry = resolveEntryReference(databaseDraft, entryRef);
  return {
    type: DatabaseChangeType.ADD_ENTRY,
    entry: {
      ...entry,
      values: Object.fromEntries(
        settings.fieldReferences
          .map((fieldRef, idx) => {
            const field = resolveFieldReference(databaseDraft, fieldRef);
            const cell = importResult.rows?.[entryIdx]?.cols?.[idx];
            const value = cell?.value || {
              value: cell?.text || getInitialValue(DatabaseFieldType.TEXT).value,
              type: DatabaseFieldType.TEXT,
            };
            return field?.id && value ? [field.id, value] : [];
          })
          .filter(([fieldId, value]) => fieldId && value)
      ),
    },
  } as DatabaseChange;
};

/**
 *  Iterates over the fields and creates a change for replacing the value of the selected cells with the pasted content.
 * */
const handleFieldChanges = (
  entryIdx: number,
  databaseDraft: DatabaseDraft,
  settings: ImportSettings,
  importResult: ImportResult,
  store: SimpleStore<DefaultRootState>,
  currentViewId: string | undefined
): DatabaseChange[] => {
  let changes: DatabaseChange[] = [];
  settings.fieldReferences.forEach((fieldRef, fieldIdx) => {
    const field = resolveFieldReference(databaseDraft, fieldRef);
    const shouldCreateField = fieldRef?.type === ImportFieldMode.CREATE;
    const cell = importResult.rows?.[entryIdx]?.cols?.[fieldIdx];
    if (cell === undefined) {
      return;
    }

    if (shouldCreateField && field) {
      const createChange: DatabaseChange = {
        type: DatabaseChangeType.ADD_FIELD,
        field,
        currentViewId,
      };
      changes.push(createChange);
    }
    const fieldChanges = getValueChanges(
      cell,
      settings,
      entryIdx,
      fieldIdx,
      databaseDraft,
      store
    );
    changes = [...changes, ...fieldChanges];
  });
  return changes;
};

const getNewTags = (selectOptions: SelectOption[], tags: string[]) => {
  const selectOptionIds = selectOptions.map((option) => option.id ?? option.label);
  return tags.filter((string) => {
    return !selectOptionIds.includes(string);
  });
};

interface TagCreationData {
  newTags: Set<string>;
  cellIndicesWithNewTags: number[];
}

/** For each cell in importValues, determines if the cell has any new tags.
 * Returns a mapping of fieldIds to new tags to create + cells to replace with
 * new tag IDs for that field. */
const getTagsToCreate = (
  importValues: ImportValues[],
  database: Database
): Record<string, TagCreationData> => {
  const tagsToCreate: Record<string, TagCreationData> = {};

  importValues.forEach((importValue, index) => {
    const field = getDatabaseFieldOptional(database, importValue.fieldId);

    if (field && field.type === DatabaseFieldType.SELECT && importValue.value?.value) {
      const newTags = getNewTags(
        field.settings.selectOptions,
        (importValue.value as DatabaseValue<DatabaseFieldType.SELECT>)?.value
      );

      if (newTags.length > 0) {
        if (tagsToCreate[field.id]) {
          newTags.forEach((tag) => tagsToCreate[field.id].newTags.add(tag));
          tagsToCreate[field.id].cellIndicesWithNewTags.push(index);
        } else {
          tagsToCreate[field.id] = {
            newTags: new Set(newTags),
            cellIndicesWithNewTags: [index],
          };
        }
      }
    }
  });

  return tagsToCreate;
};

const createNewTagChange = (
  field: DatabaseField,
  label: string
): {
  newTagOption: SelectOption;
  change: DatabaseChange;
} => {
  const newTagOption = { ...getNewOption(field.settings.selectOptions), label: label };

  const change = {
    type: DatabaseChangeType.SET_SELECT_OPTIONS,
    fieldId: field.id,
    newValue: [...field.settings.selectOptions, newTagOption],
    change: {
      type: MultiValueChangeType.APPEND,
      value: newTagOption,
    },
  } as DatabaseChange;

  return { newTagOption, change };
};

const replaceTagLabelsWithIds = (
  cell: ImportValues,
  labelsToIds: Record<string, string>
): void => {
  const value = cell.value as DatabaseValue<DatabaseFieldType.SELECT>;
  const tags = value.value;
  const updatedTags = tags.map((label) => labelsToIds[label] || label);
  value.value = updatedTags;
};

/** For each fieldId, generates an ID and color for each new tag. Now that we
have an ID for the new tags, updates the cells in importValues to use the ID
instead of the label for new tags. Returns a changeset of changes to create new tags as well
as changes to turn on "Allow tag creation" in field settings.  */
const getCreateNewTagChanges = (
  tagsToCreate: Record<string, TagCreationData>,
  database: Database,
  importValues: ImportValues[]
): {
  newTagOptionChanges: DatabaseChange[];
  allowTagCreationChanges: DatabaseChange[];
} => {
  const newTagOptionChanges: DatabaseChange[] = [];
  const allowTagCreationChanges: DatabaseChange[] = [];

  for (const fieldId in tagsToCreate) {
    const { newTags, cellIndicesWithNewTags } = tagsToCreate[fieldId];
    const field = getDatabaseFieldOptional(database, fieldId);
    if (!field) {
      continue;
    }
    const newTagsToIds: Record<string, string> = {};

    for (const tag of newTags) {
      const { newTagOption, change } = createNewTagChange(field, tag);
      newTagOptionChanges.push(change);
      newTagsToIds[tag] = newTagOption.id ?? newTagOption.label;
    }

    if (newTags.size > 0 && !field.settings.creatable) {
      const change = {
        type: DatabaseChangeType.SET_IS_CREATABLE,
        fieldId,
        isCreatable: true,
      } as DatabaseChange;
      allowTagCreationChanges.push(change);
    }

    for (const index of cellIndicesWithNewTags) {
      replaceTagLabelsWithIds(importValues[index], newTagsToIds);
    }
  }

  return {
    newTagOptionChanges,
    allowTagCreationChanges,
  };
};

/**
 * Iterates over the entries and creates a change for replacing the value of the selected cells with the pasted content.
 * If the pasted content area is larger than the selected area, the remaining cells will be filled with the pasted content.
 * If the selected area is larger than the pasted content, the extra cells will be unchanged.
 * */
export const createRangedChanges = (
  settings: ImportSettings,
  importResult: ImportResult,
  databaseDraft: DatabaseDraft,
  store: SimpleStore<DefaultRootState>,
  currentViewId: string | undefined
) => {
  let changes: DatabaseChange[] = [];

  settings.entryReferences.forEach((entryRef, entryIdx) => {
    if (entryRef?.type === ImportEntryMode.CREATE) {
      const addEntryChange = handleAddEntryChange(
        entryIdx,
        databaseDraft,
        settings,
        importResult,
        entryRef
      );
      changes = [...changes, addEntryChange];
    } else {
      const fieldChanges = handleFieldChanges(
        entryIdx,
        databaseDraft,
        settings,
        importResult,
        store,
        currentViewId
      );
      changes = [...changes, ...fieldChanges];
    }
  });

  return changes;
};

/**
 * Determines whether the multi setting should be turned on based on the values pasted
 **/
const shouldUpdateMultiSetting = (importValues: ImportValues[], database: Database) => {
  return importValues.some((importValue) => {
    const field = getDatabaseFieldOptional(database, importValue.fieldId);

    return importValue.isMulti && !field?.settings.multi;
  });
};

/**
 * Determines whether the pasted content is overwriting existing content
 **/
const isPastingOverExistingContent = (
  databaseChangeset: DatabaseChangeset,
  database: Database,
  state: DefaultRootState
) => {
  return databaseChangeset.some((changeset) => {
    if (changeset.type === DatabaseChangeType.SET_VALUE) {
      const field = getDatabaseFieldOptional(database, changeset.fieldId);
      const entry = getDatabaseEntryOptional(database, changeset.entryId);
      if (field && entry) {
        const value = getDatabaseValue(database, field, entry);
        return !isEmptyValue({ database, field, entry, value }, state);
      }

      return false;
    }
  });
};

/**
 * Determines whether the pasted content matches the field type
 */
const doesPasteContentTypeMatchField = (
  importValues: ImportValues[],
  database: Database,
  changeset: DatabaseChangeset,
  state: DefaultRootState
) => {
  let isPastingContentThatDoesNotMatch = false;

  for (const importValue of importValues) {
    const field = getDatabaseFieldOptional(database, importValue.fieldId);

    const convertedPastedValue = changeset
      .filter((change) => change.type === DatabaseChangeType.SET_VALUE)
      .find(
        (change) =>
          change.fieldId === importValue.fieldId && change.entryId === importValue.entryId
      )?.value;

    if (importValue.entryId) {
      const entry = getDatabaseEntryOptional(database, importValue.entryId);
      if (field && entry && convertedPastedValue) {
        const convertedPastedValueIsEmpty = isEmptyValue(
          { database, field, entry, value: convertedPastedValue },
          state
        );

        if (importValue.text !== '' && convertedPastedValueIsEmpty) {
          isPastingContentThatDoesNotMatch = true;
          break;
        }
      }
    }
  }

  return isPastingContentThatDoesNotMatch;
};

// TODO: to be refactored in NCTD-3034
const createMentionsNotificationOnPaste = async (
  importValues: ImportValues[],
  database: Database,
  contentId: string,
  formatMessage: MessageFormatter,
  addFlag: (flag: AddFlagProps) => void
) => {
  const mentions = [];

  for (const importValue of importValues) {
    const field = getDatabaseFieldOptional(database, importValue.fieldId);

    if (
      field &&
      field.type === DatabaseFieldType.USER &&
      field.settings.mentionNotificationEnabled
    ) {
      const value = importValue?.value?.value;

      if (isUserArray(value)) {
        const user = value[0];
        const mentionedUserAccountId = user.accountId;
        const mentionLocalId = user._id;
        const displayName = importValue.text;

        mentions.push({
          mentionedUserAccountId,
          mentionLocalId,
          displayName,
        });
      }
    }
  }

  if (mentions.length) {
    await createMentionNotification({
      contentId,
      mentions,
      formatMessage,
      addFlag,
    });
  }
};

/**
 * Determines the set of fields that needs the field setting "multi" to be set to true
 */
const getFieldSettingMultiChanges = (
  importValues: ImportValues[],
  database: Database
) => {
  const fieldSettingMultiChanges: DatabaseChange[] = [];

  for (const importValue of importValues) {
    const field = getDatabaseFieldOptional(database, importValue.fieldId);

    if (importValue.isMulti && importValue.fieldId && field && !field.settings.multi) {
      const change = {
        type: DatabaseChangeType.SET_IS_MULTI,
        fieldId: importValue.fieldId,
        isMulti: true,
      } as DatabaseChange;

      fieldSettingMultiChanges.push(change);
    }
  }

  return fieldSettingMultiChanges;
};

/**
 * Determines the last field id that was updated by the changeset
 */
const getLastUpdatedFieldId = (
  addFieldChangeset: DatabaseChange[],
  setValuesChangeset: DatabaseChange[]
) => {
  const lastNewFieldId = getLastFieldIdByChangeType(
    addFieldChangeset,
    DatabaseChangeType.ADD_FIELD
  );
  const lastExistingFieldId = getLastFieldIdByChangeType(
    setValuesChangeset,
    DatabaseChangeType.SET_VALUE
  );

  return lastNewFieldId || lastExistingFieldId;
};

/**
 * Determines the last entry id that was updated by the changeset
 */
const getLastUpdatedEntryId = (setValuesChangeset: DatabaseChange[]) => {
  const lastNewEntryId = getLastEntryIdByChangeType(
    setValuesChangeset,
    DatabaseChangeType.ADD_ENTRY
  );
  const lastExistingEntryId = getLastEntryIdByChangeType(
    setValuesChangeset,
    DatabaseChangeType.SET_VALUE
  );

  return lastNewEntryId || lastExistingEntryId;
};

const doSetSoftSelectionOnPastedContent =
  ({
    changesets,
    settings,
    contentId,
  }: {
    changesets: Record<string, DatabaseChange[]>;
    settings: ImportSettings;
    contentId: string;
  }): Dispatcher<void> =>
  (dispatch) => {
    const { createFieldsChangeSet, setValuesChangeset } = changesets;

    const lastUpdatedFieldId = getLastUpdatedFieldId(
      createFieldsChangeSet,
      setValuesChangeset
    );
    const lastUpdatedEntryId = getLastUpdatedEntryId(setValuesChangeset);

    if (lastUpdatedFieldId && lastUpdatedEntryId) {
      const firstFieldInSelection = settings.fieldReferences[0];
      const firstEntryInSelection = settings.entryReferences[0];
      if (firstFieldInSelection && firstEntryInSelection) {
        dispatch(deselectAll());
        if (
          firstFieldInSelection.type === ImportFieldMode.EXISTING &&
          firstEntryInSelection.type === ImportEntryMode.EXISTING
        ) {
          dispatch(
            selectValueSingle({
              fieldId: firstFieldInSelection.fieldId,
              entryId: firstEntryInSelection.entryId,
            })
          );
        } else if (
          firstFieldInSelection.type === ImportFieldMode.CREATE &&
          firstEntryInSelection.type === ImportEntryMode.CREATE
        ) {
          dispatch(
            selectValueSingle({
              fieldId: firstFieldInSelection.field.id,
              entryId: firstEntryInSelection.entry.id,
            })
          );
        }
        dispatch(setSoftSelection({ value: true }));
        dispatch(
          selectValueRange({
            contentId,
            fieldId: lastUpdatedFieldId,
            entryId: lastUpdatedEntryId,
          })
        );
      }
    }
  };

/**
 * Saves the pasted content to the database draft.
 **/
const doSavePastedContent =
  (
    contentId: string,
    scope: string,
    parsedFile: ImportResult,
    settings: ImportSettings,
    currentViewId: string | undefined,
    showPasteSnackbar: (
      flagId: string,
      messageId: keyof typeof messages,
      type: 'warning' | 'error',
      options?: Partial<{
        showIcon?: boolean;
        undoMessageId?: keyof typeof messages;
      }>
    ) => void,
    formatMessage: MessageFormatter,
    addFlag: (flag: AddFlagProps) => void
  ): Dispatcher<Promise<void> | { tag: 'UNKNOWN_FILE_TYPE'; fileType: string }> =>
  async (dispatch, getState) => {
    const state = getState();
    const database = selectDatabaseByContentIdRequired(getState(), { contentId });
    const cols = parsedFile.rows.map((row) => row.cols);
    const importValues: ImportValues[] = Array.from(cols).flat();

    const tagsToCreate: Record<string, TagCreationData> = getTagsToCreate(
      importValues,
      database
    );

    const { allowTagCreationChanges, newTagOptionChanges } = getCreateNewTagChanges(
      tagsToCreate,
      database,
      importValues
    );

    await createMentionsNotificationOnPaste(
      importValues,
      database,
      contentId,
      formatMessage,
      addFlag
    );

    // create and dispatch changesets
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });

    const changes = createRangedChanges(
      settings,
      parsedFile,
      databaseDraft,
      {
        dispatch,
        getState,
      },
      currentViewId
    );

    const draftedChangesets = selectDraftedChangeset(state, { contentId, scope });
    const createFieldsChangeSet = addChangeToChangeset(
      draftedChangesets,
      ...changes.filter((change) => change.type === DatabaseChangeType.ADD_FIELD)
    );
    const setValuesChangeset = addChangeToChangeset(
      draftedChangesets,
      ...changes.filter((change) => change.type !== DatabaseChangeType.ADD_FIELD)
    );
    const multiChangeset = addChangeToChangeset(
      draftedChangesets,
      ...getFieldSettingMultiChanges(importValues, database)
    );
    const newTagChangeset = addChangeToChangeset(
      draftedChangesets,
      ...newTagOptionChanges
    );
    const allowTagCreationChangeset = addChangeToChangeset(
      draftedChangesets,
      ...allowTagCreationChanges
    );

    // Prevent to save this changesets in one collabDatabaseBatch because
    // setting values depend on creating fields before
    // all changesets should be dispatched at the same time to have a consistent database state
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(setDraftedChangeset(contentId, scope, multiChangeset));
      });
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(setDraftedChangeset(contentId, scope, newTagChangeset));
      });
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(setDraftedChangeset(contentId, scope, allowTagCreationChangeset));
      });
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(setDraftedChangeset(contentId, scope, createFieldsChangeSet));
      });
      // set values needs to be last because "undo paste" should only undo the
      // paste and not the other changes
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(setDraftedChangeset(contentId, scope, setValuesChangeset));
      });
    });

    const isPastingOverContent = isPastingOverExistingContent(
      setValuesChangeset,
      database,
      state
    );

    const shouldFieldSettingUpdate =
      shouldUpdateMultiSetting(importValues, database) ||
      Boolean(allowTagCreationChanges.length);

    const isMismatchedPasteContent = doesPasteContentTypeMatchField(
      importValues,
      database,
      setValuesChangeset,
      state
    );

    // TODO: refactor NCTD-2912
    if (isPastingOverContent) {
      showPasteSnackbar(
        'pasted-over-existing-content',
        'snackbar-warning.pasting-over-existing-content',
        'warning'
      );
    }
    if (isMismatchedPasteContent) {
      showPasteSnackbar(
        'pasted-content-that-does-not-match',
        'snackbar-warning.pasting-content-that-doesnt-match',
        'warning'
      );
    }
    if (shouldFieldSettingUpdate) {
      showPasteSnackbar(
        'field-settings-updated',
        'snackbar-warning.field-settings-updated',
        'warning',
        {
          undoMessageId: 'snackbar-warning.undo-paste',
          showIcon: false,
        }
      );
    }

    dispatch(
      doSetSoftSelectionOnPastedContent({
        changesets: {
          createFieldsChangeSet,
          setValuesChangeset,
        },
        settings,
        contentId,
      })
    );
  };

export const doProcessPastedContent =
  (
    contentId: string,
    items: DataTransferItemList,
    scope: string,
    currentViewId: string,
    defaultFieldLabel: string,
    showPasteSnackbar: (
      flagId: string,
      messageId: keyof typeof messages,
      type?: 'warning' | 'error',
      options?: Partial<{
        showIcon?: boolean;
        undoMessageId?: keyof typeof messages;
      }>
    ) => void,
    formatMessage: MessageFormatter,
    addFlag: (flag: AddFlagProps) => void
  ): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    if (items.length === 0) {
      return;
    }

    const state = getState();
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const hasSelectedCells = selectHasSelectedCells(state, {
      contentId,
    });

    if (hasSelectedCells) {
      const selectedFields = selectSelectedEntireFieldIds(state, {
        contentId,
      });
      const selectedEntries = selectSelectedEntireEntryIds(state, {
        contentId,
      });

      const selectedFieldIds = selectedEntries.length
        ? database.fields
        : selectSelectedFieldIds(state, {
            contentId,
          });

      const selectedEntryIds = selectedFields.length
        ? database.entries
        : selectSelectedEntryIds(state, {
            contentId,
          });

      const fieldIds = database.fields;
      const entryIds = database.entries;
      const settings = getImportSettingsFromSelection(
        selectedFieldIds,
        selectedEntryIds,
        fieldIds,
        entryIds
      );

      const imageItems = Array.from(items).filter((item) =>
        item.type.startsWith('image/')
      );

      // early return because image handling is done in AttachmentValueEdit.tsx
      if (imageItems.length) {
        return;
      }

      // TODO: Refactor in NCTD-3029
      const pasteSupportedItems = await readPasteContent(items);
      if (!pasteSupportedItems.success) {
        pasteContentExperience.failure();
        switch (pasteSupportedItems.error.tag) {
          case 'UNSUPPORTED_MIME_TYPE':
            logWarning(
              new Error(`Unsupported mime type ${pasteSupportedItems.error.fileType}.`)
            );
            showPasteSnackbar(
              'cannot-read-pasted-content',
              'snackbar-error-message.cannot-read-pasted-content'
            );
            return;
          case 'UNEXPECTED_DATA_TYPE':
            logWarning(new Error('Unexpected data type.'));
            showPasteSnackbar(
              'unexpected-data-type',
              'snackbar-error-message.unexpected-data-type'
            );
            return;
          default:
            return unreachable(pasteSupportedItems.error);
        }
      }

      const pasteContent = parsePasteContent(pasteSupportedItems.items);
      if (!pasteContent.success) {
        pasteContentExperience.failure();
        logWarning(new Error('Pasted content cannot be read.'));
        showPasteSnackbar(
          'cannot-read-pasted-content',
          'snackbar-error-message.cannot-read-pasted-content'
        );
        return;
      }

      const importResult = applyImportSettings(
        database,
        pasteContent.file,
        settings,
        getState()
      );

      if (!importResult.success) {
        pasteContentExperience.failure();
        switch (importResult.error.tag) {
          case 'UNKNOWN_FILE_TYPE':
            logWarning(new Error(`Unknown file type: ${importResult.error.fileType}.`));
            return;
          default:
            return unreachable(importResult.error.tag);
        }
      }

      const settingsWithMissingItems = getImportSettingsWithMissingItems(
        selectedFieldIds,
        selectedEntryIds,
        fieldIds,
        entryIds,
        database,
        importResult.result,
        defaultFieldLabel
      );

      dispatch(
        doSavePastedContent(
          contentId,
          scope,
          importResult.result,
          settingsWithMissingItems,
          currentViewId,
          showPasteSnackbar,
          formatMessage,
          addFlag
        )
      );

      pasteContentExperience.success();
    }
  };

export const doSaveImport =
  (
    contentId: string,
    parentScope: string,
    scope: string,
    importedFile: ParsedImportFile,
    settings: ImportSettings,
    currentViewId: string
  ): Dispatcher<void | {
    tag: 'UNKNOWN_FILE_TYPE';
    fileType: string;
  }> =>
  (dispatch, getState) => {
    const state = getState();
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const parsed = applyImportSettings(database, importedFile, settings, state);
    if (!parsed.success) {
      return parsed.error;
    }

    const newFieldsChangeset = settings.fieldReferences.flatMap(
      (ref): DatabaseChange[] => {
        if (ref?.type === ImportFieldMode.CREATE) {
          return [
            {
              type: DatabaseChangeType.ADD_FIELD,
              field: ref.field,
              currentViewId,
            },
          ];
        } else {
          return [];
        }
      }
    );

    const newEntriesChangeset = parsed.result.rows.flatMap((row): DatabaseChange[] =>
      row.isSelected
        ? [
            {
              type: DatabaseChangeType.ADD_ENTRY,
              entry: createEntry(database, undefined, {
                values: Object.fromEntries(
                  row.cols.flatMap((cell, idx) => {
                    const field = resolveFieldReference(
                      database,
                      settings.fieldReferences[idx]
                    );
                    return field && cell.value ? [[field.id, cell.value]] : [];
                  })
                ),
              }),
            },
          ]
        : []
    );

    const changeset = addChangeToChangeset(
      selectDraftedChangeset(state, { contentId, scope }),
      ...newFieldsChangeset,
      ...newEntriesChangeset
    );

    dispatch(setDraftedChangeset(contentId, parentScope, changeset));
  };
